import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { getClients } from 'api/picking/get-clients';
import { GetClientsVm } from 'api/picking/get-clients.vm';
import { ClientVm } from 'api/picking/client.vm';

const CustomersPage = () => {
  const theme = useTheme();
  const [clients, setClients] = useState<ClientVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const fetchClients = (offset: string | null, reset: boolean = false) => {
    setLoading(true);

    getClients(offset || '', appliedSearchTerm, {
      success: (response: GetClientsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setClients(reset ? response.objects : [...clients, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter clientes:', err);
        setLoading(false);
      },
    });
  };

  useEffect(() => {
    fetchClients(null, true);
  }, [appliedSearchTerm]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchClients(nextOffset);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          Clientes
        </Typography>
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Procurar clientes por nome..."
            InputProps={{
              startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
            }}
            sx={{ width: 300 }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
          />
          <Button variant="contained" onClick={handleSearchSubmit} disabled={loading}>
            Procurar
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.map((client) => (
              <TableRow key={client.id} hover>
                <TableCell>{client.id}</TableCell>
                <TableCell>{client.name}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : clients.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais clientes para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            {appliedSearchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente disponível'}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CustomersPage;
